import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoCloseSharp } from 'react-icons/io5';
import { appendSettleNodeToPipeline } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import './SettleConfigModal.css';

// Every type the Banking node offers. Without-ToD types use the fee set on the node;
// ToD and policy types take their charges from the engine / policy document.
export const SETTLE_BANKING_GROUPS = [
  {
    label: 'Without Time of Day',
    types: [
      { value: 'daily_without_tod', label: 'Daily without Time of Day' },
      { value: 'monthly_without_tod', label: 'Monthly without Time of Day' },
      { value: 'yearly_without_tod', label: 'Yearly without Time of Day' },
    ],
  },
  {
    label: 'Time of Day',
    types: [
      { value: 'daily_tod', label: 'Daily with Time of Day' },
      { value: 'monthly_tod', label: 'Monthly with Time of Day' },
      { value: 'yearly_tod', label: 'Yearly with Time of Day' },
    ],
  },
  {
    label: 'Policy banking',
    types: [
      { value: 'policy_banking_MH_2025_corrected', label: 'Policy Banking : MH 2025' },
      { value: 'policy_banking_MH_2025_new', label: 'Policy Banking : MH 2025 (New)' },
      { value: 'policy_banking_KA_2025', label: 'Policy Banking : KA 2025' },
      { value: 'policy_banking_KA_2025_old', label: 'Policy Banking : KA 2025 (Old)' },
      { value: 'policy_banking_TN_2025', label: 'Policy Banking : TN 2025' },
      { value: 'policy_banking_GH_2025', label: 'Policy Banking : GH 2025' },
    ],
  },
];

export const SETTLE_BANKING_TYPES = SETTLE_BANKING_GROUPS.flatMap((group) => group.types);

export const settleBankingTypeLabel = (value) =>
  (SETTLE_BANKING_TYPES.find((type) => type.value === value) || {}).label || value || 'not set';

export const settleBankingFeeApplies = (value) => String(value || '').endsWith('_without_tod');

export const isSettlePolicyType = (value) => String(value || '').startsWith('policy_banking_');

// What the banking rule charges, for the node card and the summary
export const settleBankingFeeText = (bankingType, bankingFee) => {
  if (!settleBankingFeeApplies(bankingType)) {
    return isSettlePolicyType(bankingType) ? 'set by the policy' : 'set by the ToD engine';
  }
  const numeric = Number(bankingFee);
  return bankingFee !== undefined && bankingFee !== null && Number.isFinite(numeric)
    ? `${Math.round(numeric * 1e6) / 1e4}%`
    : 'not set';
};

// The API takes the fee as a decimal (0.1 = 10%); the form shows a percentage.
export const feeToPercent = (fee) => {
  const numeric = Number(fee);
  return Number.isFinite(numeric) ? String(Math.round(numeric * 1e6) / 1e4) : '10';
};

export const parseFeePercent = (text) => {
  const numeric = Number(text);
  if (String(text).trim() === '' || !Number.isFinite(numeric) || numeric < 0 || numeric > 100) return null;
  return numeric / 100;
};

const SettleConfigModal = ({ isOpen, onClose, onConfigured, initialData }) => {
  const [name, setName] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [bankingType, setBankingType] = useState('yearly_without_tod');
  const [feePercent, setFeePercent] = useState('10');
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { session } = usePipeline();

  const isEdit = Boolean(initialData?.isEditing);
  const feeApplies = settleBankingFeeApplies(bankingType);

  // Reset the form each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setSourceLabel(initialData?.sourceLabel || '');
      setBankingType(initialData?.bankingType || 'yearly_without_tod');
      setFeePercent(initialData?.bankingFee !== undefined && initialData?.bankingFee !== null
        ? feeToPercent(initialData.bankingFee)
        : '10');
      setFile(null);
      setError('');
    }
  }, [isOpen, initialData?.name, initialData?.sourceLabel, initialData?.bankingType, initialData?.bankingFee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validation = validateNodeName(name);
      if (!validation.isValid) throw new Error(validation.errorMessage);

      const inputNodeName = initialData?.inputNodeName;
      if (!inputNodeName) throw new Error('Connect the Consumption node or the previous Settle node first');

      // ToD and policy types ignore the node fee, so only validate it where it is used
      let bankingFee = 0.1;
      if (settleBankingFeeApplies(bankingType)) {
        bankingFee = parseFeePercent(feePercent);
        if (bankingFee === null) throw new Error('Banking fee must be a percentage between 0 and 100');
      }

      const label = sourceLabel.trim() || name;
      if (!file && !initialData?.generationFileName) {
        throw new Error('Upload the generation CSV for this source');
      }

      const position = initialData?.position || { x: 0, y: 0 };
      if (session.pipeline_name !== 'Unsaved_Pipeline') {
        await appendSettleNodeToPipeline(session.pipeline_name, name, inputNodeName, {
          sourceLabel: label,
          bankingType,
          bankingFee,
          generationFile: file,
          replaceExisting: isEdit,
          x: position.x,
          y: position.y,
        });
      }

      onConfigured({
        name,
        sourceLabel: label,
        bankingType,
        bankingFee,
        generationFileName: file ? file.name : initialData?.generationFileName,
      });
    } catch (err) {
      console.error('Error configuring settle node:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Rendered through a portal: inside a React Flow node the transformed ancestor
  // would make `position: fixed` size the overlay to the node instead of the screen.
  return createPortal(
    <div className="settle-modal-overlay">
      <form className="settle-modal-content" onSubmit={handleSubmit}>
        <button type="button" onClick={onClose} className="settle-modal-close" aria-label="Close">
          <IoCloseSharp size={20} />
        </button>
        <h2 className="settle-modal-title">{isEdit ? 'Edit' : 'Configure'} Settle Node</h2>
        <p className="settle-modal-intro">
          Settles this source against the demand left over by the previous round. Unused energy
          lapses with the source; banking applies to this source only.
        </p>

        {error && <div className="settle-modal-error" role="alert">{error}</div>}

        <div className="settle-modal-field">
          {isEdit ? (
            <div className="settle-modal-label">
              Node Name: <span className="settle-modal-name-display">{name}</span>
            </div>
          ) : (
            <label className="settle-modal-label">
              Node Name:
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="settle-modal-input"
                placeholder="Enter node name"
                required
              />
            </label>
          )}
        </div>

        <div className="settle-modal-field">
          <label className="settle-modal-label">
            Source Label:
            <input
              type="text"
              value={sourceLabel}
              onChange={(e) => setSourceLabel(e.target.value)}
              className="settle-modal-input"
              placeholder={name || 'Shown in summaries, e.g. Plant A'}
            />
          </label>
          <div className="settle-modal-hint">Must be unique across Settle nodes.</div>
        </div>

        <div className="settle-modal-row">
          <div className="settle-modal-field">
            <label className="settle-modal-label">
              Banking Type:
              <select
                value={bankingType}
                onChange={(e) => setBankingType(e.target.value)}
                className="settle-modal-select"
              >
                {SETTLE_BANKING_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.types.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>
          <div className="settle-modal-field settle-modal-fee">
            <label className="settle-modal-label">
              Banking Fee (%):
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={feeApplies ? feePercent : ''}
                onChange={(e) => setFeePercent(e.target.value)}
                className="settle-modal-input"
                disabled={!feeApplies}
                placeholder={feeApplies ? '' : 'n/a'}
              />
            </label>
          </div>
        </div>
        {!feeApplies && (
          <div className="settle-modal-hint settle-modal-rule-hint">
            {isSettlePolicyType(bankingType)
              ? 'Fees and slots come from the policy document. Policy banking needs one full calendar year of data.'
              : 'The ToD engine uses fixed peak/off-peak hours and its own 8% inter-slot charge.'}
          </div>
        )}

        <div className="settle-modal-field">
          <label className="settle-modal-label">
            Generation CSV{isEdit ? ' (leave empty to keep the current file)' : ''}:
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="settle-modal-input"
              aria-label="Generation CSV"
            />
          </label>
          {isEdit && initialData?.generationFileName && !file && (
            <div className="settle-modal-hint">Current file: {initialData.generationFileName}</div>
          )}
          <div className="settle-modal-hint">
            Needs Date, Time and Month columns and exactly one generation column, on the same
            timestamps as the consumption profile.
          </div>
        </div>

        <div className="settle-modal-button-container">
          <button type="submit" className="settle-modal-configure-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Configure'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default SettleConfigModal;
